import { environment } from 'environments/environment';
/**
 * Unified Search Service
 * Coordinates all search backends:
 * - Local quick actions (instant)
 * - Registry-based videos (instant)
 * - AI intent classification (200-500ms)
 * - Knowledge base (dummy for now, Pinecone RAG later)
 * - Recent items (instant)
 */

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { AppKnowledgeService } from './app-knowledge.service';
import { QuickActionsService, QuickAction } from './quick-actions.service';
import { IntentClassifierService } from './intent-classifier.service';
import { ActionExecutorService } from './action-executor.service';
import { FeatureDiscoveryService } from './feature-discovery.service';
import { APIService } from '../../API.service.custom';
import { ASSISTANT_ACTION_DEFINITIONS, VIDEO_BASE_URL } from './assistant-actions.registry';

import {
  UnifiedSearchResults,
  QuickActionResult,
  VideoResult,
  KnowledgeResult,
  AIHelpResult,
  SuggestionResult,
  RecentResult
} from '../models/unified-results.models';

@Injectable({ providedIn: 'root' })
export class UnifiedSearchService {

  private recentItems: RecentResult[] = [];
  private readonly MAX_RECENT = 5;
  private lastQueryCategory?: string;
  private lastQueryCategoryReason?: string;

  constructor(
    private router: Router,
    private knowledgeService: AppKnowledgeService,
    private quickActionsService: QuickActionsService,
    private intentClassifier: IntentClassifierService,
    private actionExecutor: ActionExecutorService,
    private discoveryService: FeatureDiscoveryService,
    private api: APIService,
    private sanitizer: DomSanitizer
  ) {
    this.loadRecentItems();
  }

  // ============================================================
  // UNIFIED SEARCH
  // ============================================================

  /**
   * Main search function - fires all backends in parallel
   */
  /**
   * Phase 1: Quick actions, videos, intent — renders immediately.
   * Knowledge (RAG) is fetched separately via searchKnowledge().
   */
  async search(
    query: string,
    context: { currentRoute: string; accountId: string; userId: string }
  ): Promise<UnifiedSearchResults> {

    this.lastQueryCategory = undefined;
    this.lastQueryCategoryReason = undefined;

    if (!query || !query.trim()) {
      return this.getDefaultResults(context.currentRoute);
    }

    const queryTrimmed = query.trim();

    try {
      const quickActionsResults = await this.searchQuickActions(queryTrimmed, context);

      const matchedIds = quickActionsResults.map(qa => qa.id);
      const videos = this.getVideosFromMatchedActions(matchedIds);

      // Derive AI Help from matched quick actions that have workflows
      const aiHelp = this.buildAIHelpFromMatches(matchedIds);

      return {
        discovery: [],
        quickActions: quickActionsResults,
        videos,
        knowledge: null,  // filled async by searchKnowledge()
        aiHelp,
        suggestions: [],
        recent: this.getRecentItems(queryTrimmed),
        routeName: this.getRouteDisplayName(context.currentRoute),
        queryCategory: this.lastQueryCategory,
        queryCategoryReason: this.lastQueryCategoryReason
      };

    } catch (error) {
      console.error('[Unified Search] Error:', error);
      return {
        discovery: [],
        quickActions: await this.searchQuickActions(queryTrimmed, context),
        videos: [],
        knowledge: null,
        aiHelp: null,
        suggestions: [],
        recent: this.getRecentItems(queryTrimmed),
        routeName: this.getRouteDisplayName(context.currentRoute),
        queryCategory: this.lastQueryCategory,
        queryCategoryReason: this.lastQueryCategoryReason
      };
    }
  }

  /**
   * Phase 2: RAG knowledge lookup — called separately so it doesn't block Phase 1.
   */
  async searchKnowledge(query: string, accountId: string): Promise<KnowledgeResult | null> {
    if (!query || !query.trim()) return null;
    return this.getKnowledgeResult(query.trim(), accountId);
  }

  // ============================================================
  // QUICK ACTIONS SEARCH (LLM first, local fallback)
  // ============================================================

  private async searchQuickActions(
    query: string,
    context?: { accountId: string; userId: string }
  ): Promise<QuickActionResult[]> {

    // 1. Try LLM/embedding search first (best quality)
    try {
      const embeddingMatches = await this.getEmbeddingQuickActionMatches(query, context);
      if (embeddingMatches.queryCategory) {
        this.lastQueryCategory = embeddingMatches.queryCategory;
        this.lastQueryCategoryReason = embeddingMatches.queryCategoryReason;
      }
      if (embeddingMatches.results.length > 0) {
        return embeddingMatches.results.slice(0, 5);
      }
    } catch (error) {
      console.warn('[Unified Search] LLM search failed, falling back to local:', error);
    }

    // 2. Fallback: local search (instant, no network)
    return this.searchQuickActionsLocal(query);
  }

  private searchQuickActionsLocal(query: string): QuickActionResult[] {
    const aggregated: QuickActionResult[] = [];

    // 1. Search routes
    const routes = this.knowledgeService.searchRoutes(query);
    for (const route of routes.slice(0, 3)) {
      aggregated.push({
        type: 'quick-action',
        id: `route-${route.path}`,
        icon: this.getIconForRoute(route.path),
        title: route.displayName,
        subtitle: route.description,
        category: 'Navigate',
        route: route.path
      });
    }

    // 2. Search quick actions (Speed Dial FAB)
    const quickActions = this.quickActionsService.searchActions(query);
    for (const qa of quickActions.slice(0, 3)) {
      aggregated.push(this.buildQuickActionResult(qa));
    }

    // 3. Search knowledge actions
    const actions = this.knowledgeService.searchActions(query);
    for (const action of actions.slice(0, 2)) {
      aggregated.push({
        type: 'quick-action',
        id: action.id,
        icon: this.getIconForCategory(action.category),
        title: action.displayName,
        subtitle: action.description,
        category: this.capitalizeCategory(action.category),
        route: action.route
      });
    }

    return this.dedupeQuickActionResults(aggregated).slice(0, 5);
  }

  private buildQuickActionResult(action: QuickAction): QuickActionResult {
    return {
      type: 'quick-action',
      id: action.id,
      icon: this.quickActionsService.getEmojiForIcon(action.icon),
      title: action.text,
      subtitle: action.tooltip,
      category: 'Quick Action',
      route: action.route || action.rootRoute,
      component: action.component,
      dialogType: action.dialogType,
      closeOnClickOutside: action.closeOnClickOutside,
      dialogConfig: action.dialogConfig
    };
  }

  // ============================================================
  // REGISTRY-BASED VIDEOS (Local, Instant)
  // ============================================================

  /**
   * Get videos from matched action IDs by looking up videoUrl in the registry.
   * Videos are marked as placeholders until hosting is configured.
   */
  private getVideosFromMatchedActions(matchedActionIds: string[]): VideoResult[] {
    const videos: VideoResult[] = [];
    const seen = new Set<string>();

    for (const actionId of matchedActionIds) {
      // Strip route- prefix from navigation results
      const cleanId = actionId.startsWith('route-') ? actionId.substring(6) : actionId;
      const def = ASSISTANT_ACTION_DEFINITIONS.find(a => a.id === cleanId);
      if (!def) continue;

      const videoPath = def.web?.videoUrl || def.videoUrl;
      if (!videoPath || seen.has(cleanId)) continue;
      seen.add(cleanId);

      const fullVideoUrl = `${VIDEO_BASE_URL}/${videoPath}`;
      videos.push({
        type: 'video',
        id: `${cleanId}-video`,
        title: def.text,
        description: def.tooltip,
        duration: 'Tutorial',
        webVideoLink: this.sanitizer.bypassSecurityTrustResourceUrl(fullVideoUrl),
        isPlaceholder: false,
        sourceFile: videoPath,
        relatedAction: {
          label: `Open ${def.text}`,
          route: def.route || def.rootRoute
        }
      });
    }

    return videos.slice(0, 3);
  }

  // ============================================================
  // KNOWLEDGE BASE (RAG via support.md in Pinecone)
  // ============================================================

  private async getKnowledgeResult(query: string, accountId: string): Promise<KnowledgeResult | null> {
    if (!environment.supportKnowledgeFileIds.length) return null;
    try {
      const response = await this.api.RagAnswer({
        accountId,
        query,
        fileIds: environment.supportKnowledgeFileIds,
        topK: 3
      });

      if (!response?.answer) return null;

      return {
        type: 'knowledge',
        answer: response.answer
      };
    } catch (error) {
      console.warn('[Unified Search] RAG knowledge lookup failed:', error);
      return null;
    }
  }


  // ============================================================
  // AI HELP (derived from matched quick actions with workflows)
  // ============================================================

  private buildAIHelpFromMatches(matchedIds: string[]): AIHelpResult | null {
    // Find registry actions that match AND have a workflowId
    const workflowActions = matchedIds
      .map(id => ASSISTANT_ACTION_DEFINITIONS.find(a => a.id === id))
      .filter(a => a?.workflowId)
      .slice(0, 3); // Show up to 3 workflow guides

    if (workflowActions.length === 0) {
      return null;
    }

    return {
      type: 'ai-help',
      message: workflowActions.length === 1
        ? 'I can guide you through this step-by-step'
        : 'I can guide you through these step-by-step',
      mode: 'guide',
      workflowId: workflowActions[0].workflowId,
      workflowSteps: workflowActions[0].steps?.length,
      actions: workflowActions.map(action => ({
        label: workflowActions.length === 1 ? 'Show Me How' : action.text,
        type: 'workflow-replay' as const,
        icon: '▶',
        workflowId: action.workflowId,
        action: async () => {
          console.log('[AI Help] Starting workflow:', action.workflowId);
        }
      }))
    };
  }

  // ============================================================
  // RECENT ITEMS
  // ============================================================

  private getRecentItems(query?: string): RecentResult[] {
    // Account permissions can change after a quick action was stored. Do not
    // revive a now-disallowed catalog action from local recent history.
    const available = this.recentItems.filter(item =>
      item.origin !== 'quick-action' || !!this.quickActionsService.getAction(item.id));
    if (query && query.trim()) {
      // Filter recent by query
      const queryLower = query.toLowerCase();
      return available.filter(item =>
        item.title?.toLowerCase().includes(queryLower)
      );
    }
    return available;
  }

  addToRecent(item: QuickActionResult | VideoResult): void {
    const recentItem: RecentResult = {
      type: 'recent',
      id: item.id,
      title: item.title,
      subtitle: item.type === 'quick-action' ? item.subtitle : item.description,
      icon: item.type === 'quick-action' ? item.icon : '🎥',
      timestamp: new Date(),
      route: item.type === 'quick-action' ? item.route : undefined,
      origin: item.type
    };

    // Remove if already exists
    this.recentItems = this.recentItems.filter(r => r.id !== item.id);

    // Add to front
    this.recentItems.unshift(recentItem);

    // Keep only MAX_RECENT
    this.recentItems = this.recentItems.slice(0, this.MAX_RECENT);

    // Save to localStorage
    this.saveRecentItems();
  }

  private loadRecentItems(): void {
    try {
      const stored = localStorage.getItem('ai-assist-recent-unified');
      if (stored) {
        const items = JSON.parse(stored);
        // Convert timestamp strings back to Date objects, filter out corrupted entries
        this.recentItems = items
          .filter((item: any) => item.id && item.title)
          .map((item: any) => ({
            ...item,
            timestamp: new Date(item.timestamp)
          }));
      }
    } catch (error) {
      console.error('[Unified Search] Failed to load recent items:', error);
      this.recentItems = [];
    }
  }

  private saveRecentItems(): void {
    try {
      localStorage.setItem(
        'ai-assist-recent-unified',
        JSON.stringify(this.recentItems)
      );
    } catch (error) {
      console.error('[Unified Search] Failed to save recent items:', error);
    }
  }

  // ============================================================
  // DEFAULT RESULTS (Empty Query)
  // ============================================================

  private getDefaultResults(currentRoute: string): UnifiedSearchResults {
    return {
      discovery: this.discoveryService.getDiscoveryTips(currentRoute, 3),
      quickActions: this.getDefaultQuickActions(),
      videos: [],
      knowledge: null,
      aiHelp: null,
      suggestions: [],
      recent: this.recentItems,
      routeName: this.getRouteDisplayName(currentRoute),
      queryCategory: undefined,
      queryCategoryReason: undefined
    };
  }

  private getDefaultQuickActions(): QuickActionResult[] {
    const results: QuickActionResult[] = [];

    // Show quick actions from Speed Dial FAB
    const quickActions = this.quickActionsService.getDefaultActions();
    for (const qa of quickActions.slice(0, 6)) {
      results.push(this.buildQuickActionResult(qa));
    }

    return results;
  }

  private async getEmbeddingQuickActionMatches(
    query: string,
    context?: { accountId: string; userId: string }
  ): Promise<{ results: QuickActionResult[]; queryCategory?: string; queryCategoryReason?: string; normalizedQuery?: string }> {
    try {
      const response = await this.api.MapDataFields(
        JSON.stringify(query),
        JSON.stringify('FIND_MATCHING_QUICK_ACTION'),
        context?.accountId,
        context?.userId
      );

      if (!response) {
        return { results: [] };
      }

      const parsed = typeof response === 'string' ? JSON.parse(response) : response;
      let body = parsed?.body ?? parsed;
      if (typeof body === 'string') {
        try {
          body = JSON.parse(body);
        } catch {
          body = null;
        }
      }

      if (!body) {
        return { results: [] };
      }

      let queryCategory: string | undefined;
      let queryCategoryReason: string | undefined;
      let normalizedQuery: string | undefined;
      let matches: any[] = [];

      if (Array.isArray(body)) {
        matches = body;
      } else if (body && typeof body === 'object') {
        if (Array.isArray(body.matches)) {
          matches = body.matches;
        } else if (Array.isArray(body.results)) {
          matches = body.results;
        }
        queryCategory = body.queryCategory || body.category;
        queryCategoryReason = body.queryCategoryReason || body.categoryReason;
        normalizedQuery = body.normalizedQuery;
      }

      if (!Array.isArray(matches)) {
        return { results: [] };
      }

      const results: QuickActionResult[] = [];

      for (const match of matches) {
        if (!match?.id) {
          continue;
        }
        const qa = this.quickActionsService.getAction(match.id);
        if (!qa) {
          continue;
        }
        const base = this.buildQuickActionResult(qa);
        const score = typeof match.score === 'number' ? match.score : null;

        results.push({
          ...base,
          category: 'AI Match',
          badge: score ? `${Math.round(score * 100)}%` : 'AI',
          subtitle: score
            ? `${qa.tooltip} �?� ${(score * 100).toFixed(0)}% match`
            : qa.tooltip
        });
      }

      return {
        results,
        queryCategory,
        queryCategoryReason,
        normalizedQuery
      };
    } catch (error) {
      console.error('[Unified Search] Quick action embedding search failed:', error);
      return { results: [] };
    }
  }

  // ============================================================
  // HELPERS
  // ============================================================

  private getIconForRoute(path: string): string {
    const icons: Record<string, string> = {
      '/dashboard': '📊',
      '/contacts': '👥',
      '/deals': '💰',
      '/conversations': '💬',
      '/reports': '📈',
      '/settings': '⚙️',
      '/metrics': '📉',
      '/calendar': '📅',
      '/tasks': '✓',
      '/to-do': '✓'
    };
    return icons[path] || '📄';
  }

  private getIconForCategory(category: string): string {
    const icons: Record<string, string> = {
      navigation: '🧭',
      data: '💾',
      messaging: '📧',
      export: '📤',
      import: '📥',
      settings: '⚙️'
    };
    return icons[category] || '⚡';
  }

  private capitalizeCategory(category: string): string {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }

  private getRouteDisplayName(path: string): string {
    if (!path) {
      return 'this';
    }

    const cleanPath = path.split('?')[0];
    const routeDef = this.knowledgeService.getRoute(cleanPath);
    if (routeDef?.displayName) {
      return routeDef.displayName;
    }

    const segments = cleanPath.split('/').filter(Boolean);
    if (segments.length === 0) {
      return 'this';
    }

    return segments[segments.length - 1]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private dedupeQuickActionResults(items: QuickActionResult[]): QuickActionResult[] {
    const seen = new Set<string>();
    const deduped: QuickActionResult[] = [];

    for (const item of items) {
      const key = item.id;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      deduped.push(item);
    }

    return deduped;
  }
}
